import { getActivityTitle, appendAtlasBrandingToNotes, resolveActivityTimelineDates } from './pptExportHelpers'
import { getTppActivityTitle } from './pptTPPhelpers'

/**
 * Adds a legend explaining actual versus planned Activity timelines.
 *
 * Activity status remains represented by bar color.
 * Timeline certainty is represented by opacity and line style.
 *
 * Returns the legend bounds so callers can position adjacent legends.
 */
export function addTimelineTypeLegend(
    slide,
    pptx,
    {
        rightX,
        y = 0.08,
        actualLabel = 'Actual dates',
        plannedLabel = 'Includes planned dates',
        sampleColor = '999999',
        fontSize = 6,
    } = {}
) {
    if (!slide || !pptx || !Number.isFinite(rightX)) {
        return null
    }

    const sampleWidth = 0.15
    const sampleHeight = 0.08
    const labelGap = 0.05
    const labelWidth = 0.86

    const rowHeight = 0.17
    const padX = 0.07
    const padY = 0.04

    const innerWidth = sampleWidth + labelGap + labelWidth

    const totalWidth = padX * 2 + innerWidth

    const totalHeight = padY * 2 + rowHeight * 2

    const x = rightX - totalWidth

    // Legend container
    slide.addShape(pptx.ShapeType.rect, {
        x,
        y,
        w: totalWidth,
        h: totalHeight,
        fill: {
            color: 'FFFFFF',
        },
        line: {
            color: '000000',
            width: 0.75,
        },
    })

    const contentX = x + padX
    const contentY = y + padY

    // Actual dates
    slide.addShape(pptx.ShapeType.roundRect, {
        x: contentX,
        y: contentY + 0.035,
        w: sampleWidth,
        h: sampleHeight,
        fill: {
            color: sampleColor,
        },
        line: {
            color: sampleColor,
            width: 1,
        },
        rectRadius: 0.02,
    })

    slide.addText(actualLabel, {
        x: contentX + sampleWidth + labelGap,
        y: contentY,
        w: labelWidth,
        h: rowHeight,
        fontSize,
        color: '444444',
        margin: 0,
        valign: 'middle',
    })

    // Includes planned dates
    const plannedY = contentY + rowHeight

    slide.addShape(pptx.ShapeType.roundRect, {
        x: contentX,
        y: plannedY + 0.035,
        w: sampleWidth,
        h: sampleHeight,
        fill: {
            color: 'FFFFFF',
        },
        line: {
            color: sampleColor,
            width: 1.5,
        },
        rectRadius: 0.02,
    })

    slide.addText(plannedLabel, {
        x: contentX + sampleWidth + labelGap,
        y: plannedY,
        w: labelWidth,
        h: rowHeight,
        fontSize,
        color: '444444',
        margin: 0,
        valign: 'middle',
    })

    return {
        x,
        y,
        w: totalWidth,
        h: totalHeight,
        right: rightX,
        rightX,
        bottom: y + totalHeight,
    }
}

export async function createValueMessageFulfillmentGanttSlide(
    pptx,
    groupedActivities = [],
    activityStatusStyles = {},
    getStatusLabel,
    {
        title = 'Value Messages + Timeline of Activities',
        activitiesDisplayName = 'Activities',

        slideWidth = 10.0,
        slideHeight = 5.625,

        startDateOverride = new Date(2024, 0, 1),
        endDateOverride = null,

        timeSplit = null,
        maxYearsQuarter = 4,
        maxYearsHalf = 8,

        periodLabelMode = 'yearOver',
        periodFont = 6,
        yearFont = 7,

        yOffset = 0.7,
        leftWidthPct = 0.38,
        chartHeightPct = 0.68,
        rightPad = 0.0,

        maxPerSlide = 20,
        minFont = 5.5,
        maxFont = 8,
        minBarH = 0.055,
        maxBarH = 0.16,
        rowGap = 0.035,
        groupGap = 0.12,

        snapStartToPeriod = true,
        snapEndToPeriod = true,

        showLegend = true,
        legendX = null,
        legendY = 0.08,

        createActivityStatusLegendFn = null,

        includeAllActivitiesInNotes = true,
    } = {}
) {
    const groups = (groupedActivities || [])
        .map((group) => ({
            category: group?.category || group?.name || 'Uncategorized',
            activities: Array.isArray(group?.activities) ? group.activities : [],
        }))
        .filter((group) => group.activities.length)

    if (!groups.length) return

    const allActivities = groups.flatMap((group) => group.activities)

    const datedActivities = allActivities.filter((activity) => activity?._ganttStartDate && activity?._ganttEndDate)
    if (!datedActivities.length) return

    const MARGIN_X = 0.025 * slideWidth
    const LEFT_X = MARGIN_X
    const LEFT_W = leftWidthPct * slideWidth
    const TITLE_Y = 0.1 * slideHeight
    const CHART_Y = TITLE_Y + yOffset
    const CHART_X = LEFT_X + LEFT_W + 0.02 * slideWidth
    const CHART_W = 0.9 * slideWidth - (CHART_X - MARGIN_X) - rightPad
    const CHART_H = chartHeightPct * slideHeight

    const addQuarter = (d, n) => new Date(d.getFullYear(), d.getMonth() + 3 * n, 1)
    const addHalf = (d, n) => new Date(d.getFullYear(), d.getMonth() + 6 * n, 1)
    const addYear = (d, n) => new Date(d.getFullYear() + n, 0, 1)

    const floorToSplit = (d, split) => {
        if (split === 'year') return new Date(d.getFullYear(), 0, 1)
        if (split === 'half') return new Date(d.getFullYear(), d.getMonth() < 6 ? 0 : 6, 1)
        return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
    }

    const addSplit = (d, n, split) => {
        if (split === 'year') return addYear(d, n)
        if (split === 'half') return addHalf(d, n)
        return addQuarter(d, n)
    }

    const minDate = new Date(
        Math.min(...datedActivities.map((activity) => new Date(activity._ganttStartDate).getTime()))
    )

    const maxDate = new Date(Math.max(...datedActivities.map((activity) => new Date(activity._ganttEndDate).getTime())))

    let rawStart = startDateOverride instanceof Date ? startDateOverride : minDate
    let rawEnd = endDateOverride instanceof Date ? endDateOverride : maxDate

    if (rawEnd <= rawStart) rawEnd = addYear(rawStart, 1)

    let split = timeSplit
    if (!split) {
        const years = (rawEnd - rawStart) / (365.25 * 24 * 3600 * 1000)
        split = years <= maxYearsQuarter ? 'quarter' : years <= maxYearsHalf ? 'half' : 'year'
    }

    const chartStart = snapStartToPeriod ? floorToSplit(rawStart, split) : new Date(rawStart)
    const chartEnd = snapEndToPeriod ? addSplit(floorToSplit(rawEnd, split), 1, split) : new Date(rawEnd)
    const chartDuration = chartEnd - chartStart

    const periods = []
    for (let t = new Date(chartStart); t < chartEnd; t = addSplit(t, 1, split)) {
        const label =
            split === 'year'
                ? String(t.getFullYear())
                : split === 'half'
                  ? `H${Math.floor(t.getMonth() / 6) + 1}`
                  : `Q${Math.floor(t.getMonth() / 3) + 1}`

        periods.push({
            label,
            year: t.getFullYear(),
            start: new Date(t),
            end: addSplit(t, 1, split),
        })
    }

    const colW = CHART_W / periods.length

    const formatNoteDate = (value) => {
        if (!value) return 'Unknown'
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return 'Unknown'
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    const buildNotesText = () => {
        const lines = ['Activities are grouped by the value message pillar they support.', '']

        groups.forEach((group) => {
            lines.push(group.category)

            group.activities.forEach((activity, idx) => {
                const statusId =
                    getStatusLabel?.(activity) || activity?.status || activity?.activityStatus || 'Uncategorized'

                // Render the org's label for the status id ('Initiated' -> 'Active' for Otsuka).
                const status = activityStatusStyles?.[statusId]?.label || statusId

                const timelineDates = activity?._ganttTimeline || resolveActivityTimelineDates(activity)

                const start = formatNoteDate(timelineDates.resolvedStartDate)

                const end = formatNoteDate(timelineDates.resolvedEndDate)

                const startLabel = timelineDates.startDateType === 'planned' ? `${start} (Planned)` : start

                const endLabel = timelineDates.endDateType === 'planned' ? `${end} (Planned)` : end

                const scenarios = activity?._tppFulfillmentScenarios || []

                const scenarioText = scenarios.length ? ` | Scenario: ${scenarios.join(' + ')}` : ''

                lines.push(
                    `${idx + 1}. ${getActivityTitle(activity)} | Status: ${status} | Timeline: ${startLabel} – ${endLabel}`
                )
            })

            lines.push('')
        })

        return lines.join('\n')
    }

    const notesText = includeAllActivitiesInNotes ? buildNotesText() : ''

    const softTruncate = (text, cap) => {
        const t = String(text || '')
        return t.length > cap ? `${t.slice(0, cap - 1)}…` : t
    }

    const timeToX = (start, end) => {
        const ss = new Date(Math.max(new Date(start), chartStart))
        const ee = new Date(Math.max(ss, Math.min(new Date(end), chartEnd)))
        const x = CHART_X + ((ss - chartStart) / chartDuration) * CHART_W
        const w = CHART_X + ((ee - chartStart) / chartDuration) * CHART_W - x

        return [x, Math.max(0.02, w)]
    }

    const startNewSlide = () => {
        const slide = pptx.addSlide({ masterName: 'CONTENT_SLIDE' })

        slide.addText(title, {
            x: MARGIN_X,
            y: TITLE_Y - 0.1,
            w: 0.9 * slideWidth,
            h: 0.35,
            fontSize: 12,
            bold: true,
            color: '000000',
        })

        slide.addText(`${activitiesDisplayName} by Pillar`, {
            x: LEFT_X,
            y: CHART_Y - 0.26,
            w: LEFT_W,
            h: 0.18,
            fontSize: 9,
            bold: true,
            color: '222222',
        })

        if (periodLabelMode === 'yearOver') {
            periods.forEach((p, idx) => {
                slide.addText(p.label, {
                    x: CHART_X + idx * colW,
                    y: CHART_Y - 0.2,
                    w: colW,
                    h: 0.16,
                    align: 'center',
                    valign: 'middle',
                    fontSize: periodFont,
                    bold: true,
                })
            })

            const firstIdx = new Map()
            const lastIdx = new Map()

            periods.forEach((p, idx) => {
                if (!firstIdx.has(p.year)) firstIdx.set(p.year, idx)
                lastIdx.set(p.year, idx)
            })

            for (const [year, sIdx] of firstIdx.entries()) {
                const eIdx = lastIdx.get(year)

                slide.addText(String(year), {
                    x: CHART_X + sIdx * colW,
                    y: CHART_Y - 0.34,
                    w: (eIdx - sIdx + 1) * colW,
                    h: 0.12,
                    align: 'center',
                    valign: 'middle',
                    fontSize: yearFont,
                    bold: true,
                    color: '444444',
                })
            }
        }

        periods.forEach((_, idx) => {
            slide.addShape(pptx.ShapeType.line, {
                x: CHART_X + idx * colW,
                y: CHART_Y,
                w: 0.01,
                h: CHART_H,
                line: { color: '999999' },
            })
        })

        slide.addShape(pptx.ShapeType.line, {
            x: CHART_X + periods.length * colW,
            y: CHART_Y,
            w: 0.01,
            h: CHART_H,
            line: { color: '999999' },
        })

        if (showLegend && typeof createActivityStatusLegendFn === 'function') {
            try {
                const statusLegendInfo = createActivityStatusLegendFn(
                    pptx,
                    slide,
                    activityStatusStyles,
                    slideWidth,
                    legendX,
                    legendY
                )

                if (statusLegendInfo) {
                    addTimelineTypeLegend(slide, pptx, {
                        rightX: statusLegendInfo.x - 0.07,
                        y: legendY,
                    })
                }
            } catch {}
        }

        if (notesText) {
            slide.addText(
                `${activitiesDisplayName} without complete timeline information are included in slide notes.`,
                {
                    x: 1.15,
                    y: 5.32,
                    w: 1.85,
                    h: 0.18,
                    fontSize: 6,
                    italic: true,
                    color: '666666',
                }
            )

            slide.addNotes(notesText)
            appendAtlasBrandingToNotes(slide)
        }

        return slide
    }

    let slide = null
    let rowIndexOnSlide = 0
    const barH = maxBarH
    const rowStep = barH + rowGap

    function startSlideIfNeeded() {
        if (!slide || rowIndexOnSlide >= maxPerSlide) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }
    }

    function addCategoryRow(category) {
        startSlideIfNeeded()

        const y = CHART_Y + rowIndexOnSlide * rowStep

        slide.addText(category, {
            x: LEFT_X,
            y,
            w: LEFT_W,
            h: barH,
            fontSize: 7.5,
            bold: true,
            color: '000000',
            valign: 'middle',
        })

        rowIndexOnSlide += 1
    }

    function addActivityRow(activity) {
        startSlideIfNeeded()

        const y = CHART_Y + rowIndexOnSlide * rowStep
        const [x, w] = timeToX(activity._ganttStartDate, activity._ganttEndDate)

        slide.addText(softTruncate(getActivityTitle(activity), 95), {
            x: LEFT_X + 0.1,
            y,
            w: LEFT_W - 0.16,
            h: barH,
            fontSize: 6,
            color: '0A0A0A',
            valign: 'middle',
        })

        const stId = getStatusLabel?.(activity)
        const style = activityStatusStyles?.[stId] ||
            activityStatusStyles?.Uncategorized ||
            activityStatusStyles?.uncategorized || {
                fill: '999999',
                line: '999999',
            }

        const includesPlannedDates = activity?._ganttTimeline?.hasPlannedDate === true

        slide.addShape(pptx.ShapeType.roundRect, {
            x,
            y,
            w,
            h: barH,
            fill: {
                color: includesPlannedDates ? 'FFFFFF' : style.fill,
            },
            line: {
                color: style.line || style.fill,
                width: includesPlannedDates ? 2 : 1,
                dashType: includesPlannedDates ? 'dash' : 'solid',
            },
            rectRadius: 0.05,
        })

        rowIndexOnSlide += 1
    }

    groups.forEach((group) => {
        const datedGroupActivities = group.activities
            .filter((activity) => activity?._ganttStartDate && activity?._ganttEndDate)
            .sort((a, b) => new Date(a._ganttStartDate) - new Date(b._ganttStartDate))

        if (!datedGroupActivities.length) return

        const rowsNeeded = 1 + datedGroupActivities.length
        const rowsRemaining = maxPerSlide - rowIndexOnSlide

        // If category + first activity cannot fit, start a new slide
        if (slide && rowsRemaining < 2) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }

        // If a small/medium category would be awkwardly split, move it to next slide
        if (slide && rowsNeeded <= maxPerSlide && rowsNeeded > rowsRemaining) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }

        addCategoryRow(group.category)

        datedGroupActivities.forEach((activity, idx) => {
            // If the group spills to a new slide, repeat category label first
            if (rowIndexOnSlide >= maxPerSlide) {
                slide = startNewSlide()
                rowIndexOnSlide = 0
                addCategoryRow(group.category)
            }

            addActivityRow(activity)
        })
    })
}

function drawScenarioBadge(slide, pptx, number, x, y, size = 0.13) {
    slide.addShape(pptx.ShapeType.ellipse, {
        x,
        y,
        w: size,
        h: size,
        fill: { color: 'FFFFFF' },
        line: { color: '000000', pt: 0.75 },
    })

    slide.addText(String(number), {
        x,
        y: y + 0.005,
        w: size,
        h: size,
        fontSize: 5.5,
        bold: true,
        color: '000000',
        align: 'center',
        valign: 'middle',
        margin: 0,
    })
}

export function addTppScenarioBadgeLegend(slide, pptx, opts = {}) {
    const {
        x = 6.9,
        y = 0.08,
        w = 1.05,
        rowH = 0.17,
        badgeSize = 0.13,
        fontSize = 5.8,
        padX = 0.07,
        padY = 0.04,
    } = opts

    const h = padY * 2 + rowH * 2

    slide.addShape(pptx.ShapeType.rect, {
        x,
        y,
        w,
        h,
        fill: { color: 'FFFFFF' },
        line: { color: '000000', pt: 0.75 },
    })

    const rows = [
        { number: 1, label: 'Base Case' },
        { number: 2, label: 'Best Case' },
    ]

    rows.forEach((row, idx) => {
        const cy = y + padY + idx * rowH

        drawScenarioBadge(slide, pptx, row.number, x + padX, cy + 0.015, badgeSize)

        slide.addText(row.label, {
            x: x + padX + badgeSize + 0.05,
            y: cy,
            w: w - padX * 2 - badgeSize - 0.05,
            h: rowH,
            fontSize,
            color: '000000',
            valign: 'middle',
            margin: 0,
        })
    })

    return { x, y, w, h, right: x + w, bottom: y + h }
}

export async function createTppFulfillmentGanttSlide(
    pptx,
    groupedActivities = [],
    activityStatusStyles = {},
    getStatusLabel,
    {
        title = 'Timeline of Activities',
        activitiesDisplayName = 'Activities',

        slideWidth = 10.0,
        slideHeight = 5.625,

        startDateOverride = new Date(2024, 0, 1),
        endDateOverride = null,

        timeSplit = null,
        maxYearsQuarter = 4,
        maxYearsHalf = 8,

        periodFont = 6,
        yearFont = 7,

        yOffset = 0.7,
        leftWidthPct = 0.38,
        chartHeightPct = 0.64,
        rightPad = 0.0,

        maxPerSlide = 18,
        minBarH = 0.055,
        maxBarH = 0.16,
        rowGap = 0.035,

        snapStartToPeriod = true,
        snapEndToPeriod = true,

        showLegend = true,
        legendY = 0.08,
        statusLegendWidthEstimate = 2.45,
        scenarioLegendWidth = 0.82,
        legendGap = 0.07,
        createActivityStatusLegendFn = null,

        includeAllActivitiesInNotes = true,
    } = {}
) {
    const groups = (groupedActivities || [])
        .map((group) => ({
            category: group?.category || group?.name || 'Uncategorized',
            activities: Array.isArray(group?.activities) ? group.activities : [],
        }))
        .filter((group) => group.activities.length)

    if (!groups.length) return

    const allActivities = groups.flatMap((group) => group.activities)
    const datedActivities = allActivities.filter((activity) => activity?._ganttStartDate && activity?._ganttEndDate)

    if (!datedActivities.length) return

    const MARGIN_X = 0.025 * slideWidth
    const LEFT_X = MARGIN_X
    const LEFT_W = leftWidthPct * slideWidth
    const TITLE_Y = 0.1 * slideHeight
    const CHART_Y = TITLE_Y + yOffset
    const CHART_X = LEFT_X + LEFT_W + 0.02 * slideWidth
    const CHART_W = 0.9 * slideWidth - (CHART_X - MARGIN_X) - rightPad
    const CHART_H = chartHeightPct * slideHeight

    const addQuarter = (d, n) => new Date(d.getFullYear(), d.getMonth() + 3 * n, 1)
    const addHalf = (d, n) => new Date(d.getFullYear(), d.getMonth() + 6 * n, 1)
    const addYear = (d, n) => new Date(d.getFullYear() + n, 0, 1)

    const floorToSplit = (d, split) => {
        if (split === 'year') return new Date(d.getFullYear(), 0, 1)
        if (split === 'half') return new Date(d.getFullYear(), d.getMonth() < 6 ? 0 : 6, 1)
        return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
    }

    const addSplit = (d, n, split) => {
        if (split === 'year') return addYear(d, n)
        if (split === 'half') return addHalf(d, n)
        return addQuarter(d, n)
    }

    const minDate = new Date(
        Math.min(...datedActivities.map((activity) => new Date(activity._ganttStartDate).getTime()))
    )

    const maxDate = new Date(Math.max(...datedActivities.map((activity) => new Date(activity._ganttEndDate).getTime())))

    let rawStart = startDateOverride instanceof Date ? startDateOverride : minDate
    let rawEnd = endDateOverride instanceof Date ? endDateOverride : maxDate

    if (rawEnd <= rawStart) rawEnd = addYear(rawStart, 1)

    let split = timeSplit
    if (!split) {
        const years = (rawEnd - rawStart) / (365.25 * 24 * 3600 * 1000)
        split = years <= maxYearsQuarter ? 'quarter' : years <= maxYearsHalf ? 'half' : 'year'
    }

    const chartStart = snapStartToPeriod ? floorToSplit(rawStart, split) : new Date(rawStart)
    const chartEnd = snapEndToPeriod ? addSplit(floorToSplit(rawEnd, split), 1, split) : new Date(rawEnd)
    const chartDuration = chartEnd - chartStart

    const periods = []
    for (let t = new Date(chartStart); t < chartEnd; t = addSplit(t, 1, split)) {
        periods.push({
            label:
                split === 'year'
                    ? String(t.getFullYear())
                    : split === 'half'
                      ? `H${Math.floor(t.getMonth() / 6) + 1}`
                      : `Q${Math.floor(t.getMonth() / 3) + 1}`,
            year: t.getFullYear(),
            start: new Date(t),
            end: addSplit(t, 1, split),
        })
    }

    const colW = CHART_W / periods.length

    const formatNoteDate = (value) => {
        if (!value) return 'Unknown'
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return 'Unknown'
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    const buildNotesText = () => {
        const activityLabel = (activitiesDisplayName || 'activities').toLowerCase()
        const lines = [
            `All ${activityLabel}, including those without timeline information, are included in these slide notes.`,
            '',
            `${activitiesDisplayName} are grouped by attribute category.`,
            '',
            'Scenario legend: 1 = Base Case; 2 = Best Case.',
            '',
        ]

        groups.forEach((group) => {
            lines.push(group.category)

            group.activities.forEach((activity, idx) => {
                const statusId =
                    getStatusLabel?.(activity) || activity?.status || activity?.activityStatus || 'Uncategorized'

                // Render the org's label for the status id ('Initiated' -> 'Active' for Otsuka).
                const status = activityStatusStyles?.[statusId]?.label || statusId

                const timelineDates = activity?._ganttTimeline || resolveActivityTimelineDates(activity)

                const start = formatNoteDate(timelineDates.resolvedStartDate)

                const end = formatNoteDate(timelineDates.resolvedEndDate)

                const startLabel = timelineDates.startDateType === 'planned' ? `${start} (Planned)` : start

                const endLabel = timelineDates.endDateType === 'planned' ? `${end} (Planned)` : end

                const scenarios = activity?._tppFulfillmentScenarios || []

                const scenarioText = scenarios.length ? ` | Scenario: ${scenarios.join(' + ')}` : ''

                lines.push(
                    `${idx + 1}. ${getTppActivityTitle(activity)} | Status: ${status} | Timeline: ${startLabel} – ${endLabel}${scenarioText}`
                )
            })

            lines.push('')
        })

        return lines.join('\n')
    }

    const notesText = includeAllActivitiesInNotes ? buildNotesText() : ''

    const softTruncate = (text, cap) => {
        const t = String(text || '')
        return t.length > cap ? `${t.slice(0, cap - 1)}…` : t
    }

    const timeToX = (start, end) => {
        const ss = new Date(Math.max(new Date(start), chartStart))
        const ee = new Date(Math.max(ss, Math.min(new Date(end), chartEnd)))
        const x = CHART_X + ((ss - chartStart) / chartDuration) * CHART_W
        const w = CHART_X + ((ee - chartStart) / chartDuration) * CHART_W - x

        return [x, Math.max(0.02, w)]
    }

    const drawScenarioBadgesForActivity = (slide, activity, barX, barW, y, barH) => {
        const scenarios = Array.isArray(activity?._tppFulfillmentScenarios) ? activity._tppFulfillmentScenarios : []

        const nums = []

        if (scenarios.includes('base')) nums.push(1)
        if (scenarios.includes('best')) nums.push(2)

        if (!nums.length) return

        const badgeSize = 0.13
        const badgeGap = 0.025
        const totalW = nums.length * badgeSize + (nums.length - 1) * badgeGap

        const shouldPlaceInside = barW >= totalW + 0.12
        const badgeY = y + Math.max(0, (barH - badgeSize) / 2)

        let badgeX = shouldPlaceInside ? barX + barW - totalW - 0.04 : barX + barW + 0.04

        const chartRight = CHART_X + CHART_W
        if (badgeX + totalW > chartRight) {
            badgeX = Math.max(barX + 0.04, barX + barW - totalW - 0.04)
        }

        nums.forEach((num, idx) => {
            drawScenarioBadge(slide, pptx, num, badgeX + idx * (badgeSize + badgeGap), badgeY, badgeSize)
        })
    }

    const startNewSlide = () => {
        const slide = pptx.addSlide({ masterName: 'CONTENT_SLIDE' })

        slide.addText(title, {
            x: MARGIN_X,
            y: TITLE_Y - 0.1,
            w: 0.9 * slideWidth,
            h: 0.35,
            fontSize: 12,
            bold: true,
            color: '000000',
        })

        slide.addText(`${activitiesDisplayName} by Attribute Category`, {
            x: LEFT_X,
            y: CHART_Y - 0.26,
            w: LEFT_W,
            h: 0.18,
            fontSize: 9,
            bold: true,
            color: '222222',
        })

        periods.forEach((p, idx) => {
            slide.addText(p.label, {
                x: CHART_X + idx * colW,
                y: CHART_Y - 0.2,
                w: colW,
                h: 0.16,
                align: 'center',
                valign: 'middle',
                fontSize: periodFont,
                bold: true,
            })
        })

        const firstIdx = new Map()
        const lastIdx = new Map()

        periods.forEach((p, idx) => {
            if (!firstIdx.has(p.year)) firstIdx.set(p.year, idx)
            lastIdx.set(p.year, idx)
        })

        for (const [year, sIdx] of firstIdx.entries()) {
            const eIdx = lastIdx.get(year)

            slide.addText(String(year), {
                x: CHART_X + sIdx * colW,
                y: CHART_Y - 0.34,
                w: (eIdx - sIdx + 1) * colW,
                h: 0.12,
                align: 'center',
                valign: 'middle',
                fontSize: yearFont,
                bold: true,
                color: '444444',
            })
        }

        periods.forEach((_, idx) => {
            slide.addShape(pptx.ShapeType.line, {
                x: CHART_X + idx * colW,
                y: CHART_Y,
                w: 0.01,
                h: CHART_H,
                line: { color: '999999' },
            })
        })

        slide.addShape(pptx.ShapeType.line, {
            x: CHART_X + periods.length * colW,
            y: CHART_Y,
            w: 0.01,
            h: CHART_H,
            line: { color: '999999' },
        })

        if (showLegend) {
            let statusLegendInfo = null
            let scenarioLegendInfo = null

            if (typeof createActivityStatusLegendFn === 'function') {
                statusLegendInfo = createActivityStatusLegendFn(
                    pptx,
                    slide,
                    activityStatusStyles,
                    slideWidth,
                    null,
                    legendY,
                    {
                        rightMargin: 0.08,
                    }
                )
            }

            if (statusLegendInfo) {
                scenarioLegendInfo = addTppScenarioBadgeLegend(slide, pptx, {
                    x: statusLegendInfo.x - scenarioLegendWidth - legendGap,
                    y: legendY,
                    w: scenarioLegendWidth,
                })
            }

            if (scenarioLegendInfo) {
                addTimelineTypeLegend(slide, pptx, {
                    rightX: scenarioLegendInfo.x - legendGap,
                    y: legendY,
                })
            }
        }

        if (notesText) {
            const activityLabel = (activitiesDisplayName || 'activities').toLowerCase()

            slide.addText(
                `All ${activityLabel}, including those without timeline information, are included in slide notes.`,
                {
                    x: 1.15,
                    y: 5.32,
                    w: 1.85,
                    h: 0.18,
                    fontSize: 6,
                    italic: true,
                    color: '666666',
                }
            )

            slide.addNotes(notesText)
        }

        return slide
    }

    let slide = null
    let rowIndexOnSlide = 0
    const barH = maxBarH
    const rowStep = barH + rowGap

    const startSlideIfNeeded = () => {
        if (!slide || rowIndexOnSlide >= maxPerSlide) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }
    }

    const addCategoryRow = (category) => {
        startSlideIfNeeded()

        const y = CHART_Y + rowIndexOnSlide * rowStep

        slide.addText(category, {
            x: LEFT_X,
            y,
            w: LEFT_W,
            h: barH,
            fontSize: 7.5,
            bold: true,
            color: '000000',
            valign: 'middle',
        })

        rowIndexOnSlide += 1
    }

    const addActivityRow = (activity) => {
        startSlideIfNeeded()

        const y = CHART_Y + rowIndexOnSlide * rowStep
        const [x, w] = timeToX(activity._ganttStartDate, activity._ganttEndDate)

        slide.addText(softTruncate(getTppActivityTitle(activity), 95), {
            x: LEFT_X + 0.1,
            y,
            w: LEFT_W - 0.16,
            h: barH,
            fontSize: 6,
            color: '0A0A0A',
            valign: 'middle',
        })

        const stId = getStatusLabel?.(activity)
        const style = activityStatusStyles?.[stId] ||
            activityStatusStyles?.Uncategorized ||
            activityStatusStyles?.uncategorized || {
                fill: '999999',
                line: '999999',
            }

        const includesPlannedDates = activity?._ganttTimeline?.hasPlannedDate === true

        slide.addShape(pptx.ShapeType.roundRect, {
            x,
            y,
            w,
            h: barH,
            fill: {
                color: includesPlannedDates ? 'FFFFFF' : style.fill,
            },
            line: {
                color: style.line || style.fill,
                width: includesPlannedDates ? 2 : 1,
                dashType: includesPlannedDates ? 'dash' : 'solid',
            },
            rectRadius: 0.05,
        })

        drawScenarioBadgesForActivity(slide, activity, x, w, y, barH)

        rowIndexOnSlide += 1
    }

    groups.forEach((group) => {
        const datedGroupActivities = group.activities
            .filter((activity) => activity?._ganttStartDate && activity?._ganttEndDate)
            .sort((a, b) => new Date(a._ganttStartDate) - new Date(b._ganttStartDate))

        if (!datedGroupActivities.length) return

        const rowsNeeded = 1 + datedGroupActivities.length
        const rowsRemaining = maxPerSlide - rowIndexOnSlide

        if (slide && rowsRemaining < 2) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }

        if (slide && rowsNeeded <= maxPerSlide && rowsNeeded > rowsRemaining) {
            slide = startNewSlide()
            rowIndexOnSlide = 0
        }

        addCategoryRow(group.category)

        datedGroupActivities.forEach((activity) => {
            if (rowIndexOnSlide >= maxPerSlide) {
                slide = startNewSlide()
                rowIndexOnSlide = 0
                addCategoryRow(group.category)
            }

            addActivityRow(activity)
        })
    })
}
